/**
 * SbInfoTooltip - Label + info-icon tooltip component
 *
 * Renders inline children (typically a label) followed by an info icon that
 * acts as the tooltip trigger. The icon is keyboard-focusable and exposes
 * ARIA semantics so screen readers announce it as an interactive element.
 *
 * Props:
 *   - children  : Inline label content rendered before the icon.
 *   - content   : Tooltip body, forwarded to SbTooltip.
 *   - placement : Ant Design tooltip placement. Default 'right'.
 *   - iconSize  : Icon font-size in px. Default 14.
 *
 * @example
 * import { SbInfoTooltip } from 'uiCore';
 *
 * <SbInfoTooltip content="What this field means">
 *   Field label
 * </SbInfoTooltip>
 */

import React from 'react';
import { InfoCircleOutlined } from '../SbIcons/SbIcons';
import SbTooltip from './SbTooltip';
import { InfoLabelWrapper, InfoIconStyled } from './SbTooltip.styles';

const SbInfoTooltip = ({
  children,
  content,
  placement = 'right',
  iconSize = 14,
  ...rest
}) => (
  <InfoLabelWrapper>
    {children}
    <SbTooltip
      content={content}
      placement={placement}
      delay={0}
      trigger={['hover', 'focus']}
      {...rest}
    >
      <InfoIconStyled
        $size={iconSize}
        aria-label="More information"
        tabIndex={0}
        role="button"
        onKeyDown={(e) => {
          // AntD's focus trigger handles activation; preventDefault on Space
          // stops the page from scrolling when the icon has keyboard focus.
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
          }
        }}
      >
        <InfoCircleOutlined />
      </InfoIconStyled>
    </SbTooltip>
  </InfoLabelWrapper>
);

export default SbInfoTooltip;
